import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ArrowRight, User, Calendar, Tag } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const Blog: React.FC = () => {
  const blogPosts = [
    // 核心11篇博客文章 - 按照架构方案排序
    {
      id: 'chinese-names-meanings',
      title: 'Chinese Names and Meanings: Understanding the Cultural Significance',
      excerpt: 'Discover the deep meanings behind Chinese names and their cultural significance. Learn how Chinese characters create meaningful names and explore popular names.',
      readTime: '10 min read',
      publishDate: '2025-01-15',
      category: 'Meanings',
      color: 'from-red-500 to-orange-600',
      keywords: ['chinese names and meanings', 'chinese name meaning dictionary', 'beautiful chinese names']
    },
    {
      id: 'chinese-boy-names-meanings',
      title: 'Chinese Boy Names and Meanings - Deep Dive',
      excerpt: 'Deep dive into Chinese boy names and their meanings. Explore traditional, modern, and powerful male Chinese names with cultural significance.',
      readTime: '9 min read',
      publishDate: '2025-01-15',
      category: 'Boy Names',
      color: 'from-blue-500 to-indigo-600',
      keywords: ['chinese boy names and meanings', 'chinese male names meaning', 'traditional chinese boy names']
    },
    {
      id: 'beautiful-chinese-girl-names-meanings',
      title: 'Beautiful Chinese Names for Girls with Meaning',
      excerpt: 'Discover beautiful Chinese girl names with profound meanings. Explore elegant, cute, and traditional female Chinese names with cultural significance.',
      readTime: '9 min read',
      publishDate: '2025-01-15',
      category: 'Girl Names',
      color: 'from-pink-500 to-rose-600',
      keywords: ['beautiful chinese names for girl with meaning', 'beautiful chinese girl names', 'elegant chinese girl names']
    },
    {
      id: 'chinese-last-name-meanings',
      title: 'Chinese Last Name Meanings Explained',
      excerpt: 'Discover the meanings and origins of Chinese last names. Learn about common Chinese surnames, their historical significance, and cultural importance.',
      readTime: '8 min read',
      publishDate: '2025-01-15',
      category: 'Surnames',
      color: 'from-purple-500 to-indigo-600',
      keywords: ['chinese last name meanings', 'chinese surname meanings', 'chinese family name meanings']
    },
    {
      id: 'chinese-family-names-meanings',
      title: 'Chinese Family Names History',
      excerpt: 'Explore the rich history of Chinese family names spanning thousands of years. Learn about the origins, evolution, and cultural significance of Chinese surnames.',
      readTime: '8 min read',
      publishDate: '2025-01-15',
      category: 'History',
      color: 'from-amber-500 to-orange-600',
      keywords: ['chinese family names and meanings', 'chinese surname history', 'history of chinese names']
    },
    {
      id: 'my-name-in-chinese',
      title: 'My Name in Chinese - Complete Translation Guide',
      excerpt: 'Learn how to translate your English name into Chinese. Complete guide to phonetic translation, meaning-based names, and cultural considerations.',
      readTime: '10 min read',
      publishDate: '2025-01-15',
      category: 'Translation',
      color: 'from-purple-500 to-pink-600',
      keywords: ['my name in chinese', 'my name is in chinese', 'what is my chinese name']
    },
    {
      id: 'chinese-name-translation',
      title: 'Chinese Name Translation: How It Works',
      excerpt: 'Learn how Chinese name translation works. Understand phonetic translation, meaning-based selection, and cultural considerations for translating names to Chinese.',
      readTime: '8 min read',
      publishDate: '2025-01-15',
      category: 'Translation',
      color: 'from-teal-500 to-cyan-600',
      keywords: ['chinese name translation', 'chinese name translator', 'name convert to chinese']
    },
    {
      id: 'what-is-your-name-in-chinese',
      title: 'What Is Your Name in Chinese? Interactive Guide',
      excerpt: 'Find out what your name is in Chinese. Interactive guide to translating your name with phonetic matching and meaning-based selection.',
      readTime: '6 min read',
      publishDate: '2025-01-15',
      category: 'Translation',
      color: 'from-blue-500 to-cyan-600',
      keywords: ['what is your name in chinese', 'what is my name in chinese', 'translate your name to chinese']
    },
    {
      id: 'how-to-choose-chinese-name',
      title: 'How to Choose a Chinese Name: Complete Beginner\'s Guide 2025',
      excerpt: 'Learn how to choose a Chinese name that suits your personality. This comprehensive guide covers Chinese name meanings, cultural considerations, and step-by-step instructions.',
      readTime: '8 min read',
      publishDate: '2025-01-15',
      category: 'Tutorial',
      color: 'from-blue-500 to-purple-600',
      keywords: ['how to choose a chinese name', 'chinese name for beginners', 'chinese name selection guide']
    },
    {
      id: 'chinese-names-pronunciation',
      title: 'How to Pronounce Chinese Names: Complete Guide with Audio Examples',
      excerpt: 'Master Chinese name pronunciation with our comprehensive guide. Learn pinyin basics, tone marks, and common pronunciation mistakes with audio examples.',
      readTime: '12 min read',
      publishDate: '2025-01-15',
      category: 'Tutorial',
      color: 'from-green-500 to-teal-600',
      keywords: ['how to pronounce chinese names', 'chinese name pronunciation guide', 'pinyin pronunciation', 'pronounce chinese names']
    },
    {
      id: 'how-to-say-my-name-in-chinese',
      title: 'How to Say My Name Is in Chinese',
      excerpt: 'Learn how to say "My name is" in Chinese and introduce yourself properly. Complete guide with pronunciation, examples, and cultural tips.',
      readTime: '6 min read',
      publishDate: '2025-01-15',
      category: 'Tutorial',
      color: 'from-green-500 to-emerald-600',
      keywords: ['how to say my name is in chinese', 'how do you say my name is in chinese', 'my name is in chinese']
    },
    {
      id: 'chinese-names-for-foreigners',
      title: 'Chinese Names for Foreigners: Complete Guide to Choosing Your Perfect Name',
      excerpt: 'Comprehensive guide for foreigners choosing Chinese names. Learn phonetic translation, meaning-based selection, cultural considerations, and get personalized suggestions.',
      readTime: '14 min read',
      publishDate: '2025-01-15',
      category: 'International Guide',
      color: 'from-green-500 to-teal-600',
      keywords: ['chinese names for foreigners', 'chinese american names', 'how to get a chinese name as a foreigner', 'english to chinese name translation']
    },
    {
      id: 'chinese-signet-rings',
      title: 'Chinese Signet Rings Last Name Lee Crest: Family Heritage & Symbolism',
      excerpt: 'Discover the rich heritage of Chinese signet rings featuring the Lee family crest. Learn about ancient Chinese names, surname symbolism, and how signet rings preserve family identity.',
      readTime: '10 min read',
      publishDate: '2025-01-15',
      category: 'Heritage',
      color: 'from-purple-500 to-indigo-600',
      keywords: ['chinese signet rings last name lee crest', 'chinese last name lee crest', 'ancient chinese names', 'chinese surname crest']
    },
    {
      id: 'funny-chinese-names',
      title: 'Funny Chinese Names: Understanding Cultural Sensitivity & Humor',
      excerpt: 'Explore funny Chinese names and silly Chinese names with cultural context. Learn why some name combinations sound humorous and how to avoid awkward translations.',
      readTime: '8 min read',
      publishDate: '2025-01-15',
      category: 'Cultural Guide',
      color: 'from-yellow-500 to-orange-600',
      keywords: ['funny chinese names', 'silly chinese names', 'humorous chinese names', 'chinese name translation mistakes']
    }
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Names Blog - Meanings, Culture & Naming Guide"
        description="Looking for expert guidance on Chinese names? Our comprehensive blog covers everything: name meanings, cultural traditions, pronunciation guides, and step-by-step tutorials. Learn how to choose the perfect Chinese name, understand character meanings, and avoid common mistakes. 19+ in-depth articles written by Chinese naming experts. Start your journey to finding your authentic Chinese identity."
        keywords="chinese names blog, chinese name meaning guide, how to understand chinese names, chinese naming culture articles, learn about chinese names, chinese name selection tips, chinese names for beginners, chinese name pronunciation guide, chinese name articles, chinese naming blog"
        pathname="/blog"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Chinese Names Blog
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Chinese Names Blog: Your Complete Guide to Chinese Naming
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Explore comprehensive guides about Chinese names, their meanings, cultural significance, and how to choose the perfect Chinese name. Learn about naming traditions, popular names, and pronunciation tips.
            </p>

            {/* CTA Button */}
            <Link 
              to="/chinese-name-generator"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Try Our Name Generator
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Featured Topics Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Latest Articles on Chinese Names and Meanings
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article key={post.id} className="group">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                    {/* Article Image/Header */}
                    <div className={`h-48 bg-gradient-to-br ${post.color} flex items-center justify-center relative`}>
                      <div className="text-center text-white p-6">
                        <h3 className="text-xl font-bold mb-2">{post.category}</h3>
                        <div className="flex items-center justify-center gap-4 text-sm opacity-90">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.readTime}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.publishDate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4">
                        <Tag className="w-6 h-6 text-white opacity-75" />
                      </div>
                    </div>

                    {/* Article Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      {/* Keywords Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.keywords.slice(0, 2).map((keyword, index) => (
                          <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {keyword}
                          </span>
                        ))}
                      </div>

                      {/* Read More Button */}
                      <Link 
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors group"
                      >
                        Read Full Article
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Navigation Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Chinese Naming Culture & Traditions
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Featured Guide Cards */}
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Featured Chinese Name Guides</h3>
                <p className="text-gray-600 mb-4">Comprehensive tutorials for understanding Chinese names</p>
                <Link to="/chinese-name-generator" className="text-blue-600 font-semibold hover:text-blue-700">
                  Start Learning →
                </Link>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Popular Topics in Chinese Naming</h3>
                <p className="text-gray-600 mb-4">Trending discussions about Chinese name selection</p>
                <Link to="/cultural-insights" className="text-purple-600 font-semibold hover:text-purple-700">
                  Explore Culture →
                </Link>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-teal-50 rounded-xl">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Tips for Name Selection</h3>
                <p className="text-gray-600 mb-4">Professional guidance for choosing your perfect name</p>
                <Link to="/chinese-last-names" className="text-green-600 font-semibold hover:text-green-700">
                  Get Expert Tips →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Discover Your Perfect Chinese Name?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Use our AI-powered generator to create meaningful Chinese names with cultural significance
            </p>
            <Link 
              to="/chinese-name-generator"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Generate My Chinese Name
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog; 
